/**
 * Zentrales Tooltip-Inventory (Issue #73 / Paket D).
 *
 * Inhalte erklären BayBO-Begriffe, Felder und UI-Elemente kurz und
 * praxisrelevant für Brandschutz-Planer in Bayern.
 *
 * Keys folgen dem Pattern `<scope>.<feld>`:
 *   - project.*   — Projekt-Formularfelder
 *   - customer.*  — Kunden-Formularfelder
 *   - sidebar.*   — Sidebar-Cards auf der Projekt-Detailseite
 *   - status.*    — Workflow-Statuswerte
 *   - dashboard.* — KPI-Cards und Chart-Cards der Startseite
 */

export interface TooltipEntry {
  /** Kurzer Titel (optional, fett im Tooltip). */
  title?: string;
  /** Haupt-Text — kann mehrzeilig sein, wird als <p> gerendert. */
  body: string;
  /** Optionaler Verweis auf BayBO/MBO-Paragraph. */
  legalRef?: string;
}

export const TOOLTIPS: Record<string, TooltipEntry> = {
  // ────────────────────────────────────────────────────────────
  // project.*
  // ────────────────────────────────────────────────────────────
  'project.title': {
    title: 'Projekt-Titel',
    body:
      'Eindeutige Bezeichnung des Brandschutznachweises. Empfehlung: Nutzungsart + ' +
      'Standort, z. B. „Brandschutznachweis Wohnhaus Maximilianstr. 12, München“.',
  },
  'project.address': {
    title: 'Adresse',
    body:
      'Postanschrift des Bauvorhabens (Straße, Hausnummer, PLZ, Ort). Wird für ' +
      'Behördenkommunikation und Feuerwehr-Pläne verwendet.',
  },
  'project.gemarkung': {
    title: 'Gemarkung',
    body:
      'Katasterbezeichnung der Liegenschaft. Im Bayerischen Liegenschaftskataster ' +
      'eindeutig identifizierend, wird im Lageplan und Bauantrag benötigt.',
  },
  'project.flurNr': {
    title: 'Flur-Nummer',
    body:
      'Flurstücksnummer aus dem Liegenschaftskataster, z. B. „1234/5“. Erforderlich ' +
      'für Bauantrag und Eintragungen.',
  },
  'project.isSonderbau': {
    title: 'Sonderbau',
    body:
      'Setzen Sie den Haken, wenn das Vorhaben bauordnungsrechtlich als Sonderbau ' +
      'einzustufen ist. Ob das zutrifft, entscheidet die Landesbauordnung — die ' +
      'Fundstelle blendet der Rechtshinweis direkt unter dem Feld ein (Schalter ' +
      '„Rechtshinweise" oben rechts oder Strg+J). ' +
      'Wirkung im Formular: Der „Sonderbau-Subtyp" wird zum Pflichtfeld, und je nach ' +
      'gewähltem Subtyp blendet Ignis AI weitere Pflichtangaben ein. Zusammen mit ' +
      'Gebäudeklasse 4/5 wird zusätzlich die Verfahrensart zur Pflicht.',
  },
  'project.sonderbauTyp': {
    title: 'Sonderbau-Subtyp',
    body:
      'Pflichtfeld, sobald „Sonderbau" angekreuzt ist. Der Subtyp steuert, welche ' +
      'Zusatzfelder erscheinen und ausgefüllt werden müssen: Versammlungsstätte und ' +
      'Beherbergung → Personen- bzw. Bettenzahl; Verkaufsstätte → Verkaufsraum-Fläche; ' +
      'Industriebau → Sicherheitskategorie, Brandlast und Lagerart; Garage → ' +
      'Garage-Größe und offen/geschlossen; Hochhaus → Hochhaus-Typ und OK letzter ' +
      'Aufenthaltsraum. Ab welchen Schwellen ein Subtyp greift, steht in der jeweiligen ' +
      'Sonderbau-Verordnung bzw. Landesbauordnung — siehe Rechtshinweis am Feld.',
  },
  'project.buildingType': {
    title: 'Gebäudeart',
    body:
      'Nutzungsart des Gebäudes (Wohnen, Büro, Industrie, Sonderbau …). Steuert ' +
      'gemeinsam mit der Gebäudeklasse die Anforderungen aus der BayBO.',
    legalRef: 'BayBO Art. 2',
  },
  'project.buildingClass': {
    title: 'Gebäudeklasse (GKL)',
    body:
      'GKL 1: freistehend ≤ 7 m, ≤ 2 NE/≤ 400 m². ' +
      'GKL 2: nicht freistehend, sonst wie 1. ' +
      'GKL 3: sonstige Gebäude ≤ 7 m. ' +
      'GKL 4: ≤ 13 m, NE ≤ 400 m². ' +
      'GKL 5: alle übrigen, inkl. unterirdisch. Höhe = OK Fußboden des höchsten ' +
      'Aufenthaltsraums über Geländeoberfläche.',
    legalRef: 'BayBO Art. 2 Abs. 3',
  },
  'project.mainUse': {
    title: 'Hauptnutzung',
    body:
      'Überwiegende Nutzung des Gebäudes. Bei gemischter Nutzung gilt die flächen- ' +
      'oder personenmäßig dominierende Nutzung als Haupt-Nutzung; weitere Nutzungen ' +
      'gesondert betrachten.',
  },
  'project.fireResistance': {
    title: 'Feuerwiderstand (F-Klasse)',
    body:
      'F30 = feuerhemmend, F60 = hochfeuerhemmend, F90 = feuerbeständig, F120 = ' +
      'hochfeuerbeständig. Anforderung ergibt sich aus Gebäudeklasse: GKL 1–2 i. d. R. ' +
      'F30, GKL 3 F30, GKL 4 F60, GKL 5 F90.',
    legalRef: 'BayBO Art. 25 ff.',
  },
  'project.escapeRoutes': {
    title: 'Rettungswege',
    body:
      'Mind. 1 baulicher Rettungsweg ist Pflicht. Ab GKL 4–5 sowie bei Aufenthaltsräumen ' +
      'über 7 m Brüstungshöhe ist ein zweiter Rettungsweg erforderlich (zweiter ' +
      'baulicher Rettungsweg oder über Geräte der Feuerwehr).',
    legalRef: 'BayBO Art. 31',
  },
  'project.fireSections': {
    title: 'Brandabschnitte',
    body:
      'Maximale Ausdehnung von Brandabschnitten i. d. R. 40 m Länge. Brandwände sind ' +
      'feuerbeständig, raumabschließend und schließen das Dach 30 cm überragend ab. ' +
      'Bei Sonderbauten gelten abweichende Werte.',
    legalRef: 'BayBO Art. 28',
  },
  'project.maxEscapeLen': {
    title: 'Max. Fluchtweg-Länge',
    body:
      'Tatsächlich gemessene Lauflänge vom entferntesten Aufenthaltsort zum Ausgang ' +
      'ins Freie oder zu einem notwendigen Treppenraum. Regelfall ≤ 35 m; Sonderbauten ' +
      'können geringere Werte verlangen.',
    legalRef: 'BayBO Art. 33',
  },
  'project.emergencyExits': {
    title: 'Notausgänge',
    body:
      'Anzahl unabhängiger Ausgänge ins Freie oder in gesicherte Bereiche. Türen in ' +
      'Rettungswegen müssen in Fluchtrichtung aufschlagen und ohne Hilfsmittel öffenbar ' +
      'sein.',
  },
  'project.smokeDetection': {
    title: 'Rauchmelder',
    body:
      'In Wohnungen sind Rauchwarnmelder in Schlaf-, Kinderzimmern und Fluren als ' +
      'Rettungswege Pflicht (BayBO Art. 46 Abs. 4). Bei Sonderbauten ggf. flächendeckende ' +
      'Brandmeldeanlage erforderlich.',
    legalRef: 'BayBO Art. 46 Abs. 4',
  },
  'project.sprinklerSys': {
    title: 'Sprinkleranlage',
    body:
      'Selbsttätige Feuerlöschanlage nach DIN EN 12845 / VdS CEA 4001. In Bayern Pflicht ' +
      'bei bestimmten Sonderbauten (Verkaufsstätten > 2.000 m², Hochhäuser, große ' +
      'Versammlungsstätten) oder als Kompensation für Abweichungen.',
  },
  'project.fireAlarm': {
    title: 'Brandmeldeanlage (BMA)',
    body:
      'Automatische Anlage zur frühzeitigen Branderkennung mit Aufschaltung zur Leitstelle. ' +
      'Pflicht bei Sonderbauten (Versammlungsstätten, Hochhäuser, Krankenhäuser, ' +
      'bestimmte Verkaufsstätten). Planung nach DIN 14675.',
  },
  'project.safetyLight': {
    title: 'Sicherheitsbeleuchtung',
    body:
      'Notbeleuchtung für Rettungswege bei Stromausfall. Pflicht u. a. in Versammlungs- ' +
      'stätten, Verkaufsstätten, Beherbergungsstätten, Industriebauten und fensterlosen ' +
      'Räumen. Mindestbeleuchtungsstärke 1 lx auf Mittellinie des Rettungswegs.',
  },
  'project.rwa': {
    title: 'Rauch- und Wärmeabzug (RWA)',
    body:
      'Anlage zur Entrauchung im Brandfall. Notwendige Treppenräume in GKL 5 brauchen ' +
      'eine Öffnung von mind. 1 m² zur Rauchableitung. Bei Sonderbauten oft maschinelle ' +
      'RWA-Anlagen erforderlich.',
    legalRef: 'BayBO Art. 33 Abs. 8',
  },
  'project.waterSupply': {
    title: 'Löschwasserversorgung',
    body:
      'Grundsätzlich aus dem öffentlichen Trinkwassernetz (Hydranten). Bei unzureichender ' +
      'Leistung ergänzend aus Löschwasserteichen, Zisternen oder offenen Gewässern. ' +
      'Mindestversorgung über 2 Stunden sicherzustellen.',
    legalRef: 'DVGW W 405',
  },
  'project.waterAmount': {
    title: 'Löschwassermenge',
    body:
      'Erforderliche Löschwassermenge nach DVGW W 405: kleines Wohngebiet 48 m³/h ' +
      '(800 l/min), Gewerbe 96–192 m³/h. Aus Anzahl Geschossen × Brandabschnittsfläche ' +
      'überschlägig zu ermitteln.',
  },
  'project.hydrants': {
    title: 'Hydranten',
    body:
      'Über- oder Unterflurhydranten im max. Abstand von 80 m vom Objekt, untereinander ' +
      'max. 150 m. Mind. ein Hydrant in unmittelbarer Nähe (≤ 75 m) zur Anfahrtszone der ' +
      'Feuerwehr.',
  },
  'project.fdAccess': {
    title: 'Feuerwehrzufahrt',
    body:
      'Befestigte Zu- oder Durchfahrt für Feuerwehrfahrzeuge: lichte Höhe ≥ 3,50 m, ' +
      'lichte Breite ≥ 3,00 m, Achslast ≥ 10 t. Aufstellflächen für Drehleiter-Einsatz ' +
      'bei Gebäuden mit Brüstungshöhe > 8 m erforderlich.',
    legalRef: 'BayBO Art. 5; DIN 14090',
  },
  'project.deadline': {
    title: 'Frist',
    body:
      'Geplante Abgabe-/Fertigstellungsfrist des Brandschutznachweises. Wird in der ' +
      'Projektliste prominent angezeigt; warnt bei Überschreitung in der Übersicht.',
  },
  'project.priority': {
    title: 'Priorität',
    body:
      'Reihung nach Dringlichkeit: low (planbar) · medium (Standard) · high (Eilauftrag) · ' +
      'urgent (kritisch, Termin gefährdet). Beeinflusst Sortierung in Listen und ' +
      'Erinnerungen.',
  },

  // ────────────────────────────────────────────────────────────
  // customer.*
  // ────────────────────────────────────────────────────────────
  'customer.name': {
    title: 'Ansprechpartner',
    body:
      'Vor- und Nachname der Kontaktperson beim Kunden. Wird in Korrespondenz und ' +
      'Projektübersicht angezeigt.',
  },
  'customer.company': {
    title: 'Firma',
    body:
      'Firmenname des Kunden (Bauherr, Architekturbüro, Investor). Bei Privatkunden den ' +
      'Namen wiederholen oder „Privat“ eintragen.',
  },
  'customer.email': {
    title: 'E-Mail',
    body:
      'Primäre Kontakt-E-Mail. Wird für automatische Benachrichtigungen, ' +
      'Freigabeanfragen und PDF-Versand genutzt.',
  },
  'customer.website': {
    title: 'Website',
    body: 'Optional. Webseite des Kunden, z. B. zur Verlinkung in Berichten.',
  },
  'customer.zip': {
    title: 'Postleitzahl',
    body: '5-stellige deutsche Postleitzahl. Wird für Ortssuche und Routing genutzt.',
  },
  'customer.city': {
    title: 'Ort',
    body: 'Sitz des Kunden. Bei mehreren Niederlassungen die Rechnungsadresse eintragen.',
  },

  // ────────────────────────────────────────────────────────────
  // sidebar.*
  // ────────────────────────────────────────────────────────────
  'sidebar.completeness': {
    title: 'Vollständigkeit',
    body:
      'Anteil der ausgefüllten Pflichtangaben. 100 % heißt: nichts fehlt mehr — nicht, ' +
      'dass alles schlüssig ist. Ob die Angaben fachlich zusammenpassen, sagt die Karte ' +
      '„Fachliche Prüfung"; dort können auch bei 100 % noch Befunde stehen. ' +
      'Wird automatisch berechnet.',
  },
  'sidebar.pruefung': {
    title: 'Fachliche Prüfung',
    body:
      'Prüft die erfassten Angaben laufend gegen die hinterlegten Regeln — aus den Werten, ' +
      'die gerade im Formular stehen. Ein Befund lässt sich nicht abhaken: Er verschwindet, ' +
      'sobald der Wert geändert wird, der ihn auslöst. Fehler verhindern das Einreichen, ' +
      'Abweichungen brauchen dabei eine Begründung, Hinweise erfordern nichts.',
  },
  'sidebar.workflow': {
    title: 'Workflow',
    body:
      'Aktueller Status und mögliche nächste Aktion. Statuskette: ' +
      'Entwurf → Planung → in Prüfung → freigegeben → abgeschlossen.',
  },
  'sidebar.eckdaten': {
    title: 'Eckdaten',
    body:
      'Schnellüberblick: Erstellungs- und Änderungsdatum sowie zugewiesene Personen ' +
      '(Planer, externer Prüfer).',
  },
  'sidebar.assignments': {
    title: 'Zuweisungen',
    body:
      'Personen mit Rollen am Projekt (Projektleitung, Bearbeitung, Freigabe). Steuert ' +
      'Sichtbarkeit und Bearbeitungsrechte.',
  },
  'sidebar.contacts': {
    title: 'Kontakte',
    body:
      'Externe Beteiligte: Bauherr, Architekt, Statiker, Sachverständiger, Behörde. ' +
      'Werden im PDF-Deckblatt aufgeführt.',
  },
  'sidebar.versions': {
    title: 'Versionen',
    body:
      'Versionshistorie des Brandschutznachweises. Jede Freigabe erzeugt eine neue ' +
      'Version; ältere Versionen bleiben revisionssicher abrufbar.',
  },
  'sidebar.aiInvolvement': {
    title: 'KI-Beteiligung',
    body:
      'Verteilung aller KI- oder Kopie-Vorschläge auf diesem Projekt: übernommen, mit ' +
      'eigenem Wert ersetzt, verworfen oder noch offen. Die Quote zeigt, wie viele ' +
      'Vorschläge unverändert übernommen wurden.',
  },
  'sidebar.standort': {
    title: 'Standort',
    body:
      'Lage der Baustelle auf der Karte plus Distanz und Fahrzeit ab dem Büro. ' +
      'Konnte die Adresse nicht aufgelöst werden, erscheint hier „nicht auflösbar" ' +
      'mit der Möglichkeit, das Geocoding erneut anzustoßen; ist sie noch nicht ' +
      'verortet, lässt sie sich per „Jetzt geocoden" sofort nachziehen.',
  },

  // ────────────────────────────────────────────────────────────
  // status.*
  // ────────────────────────────────────────────────────────────
  'status.draft': {
    title: 'Entwurf',
    body:
      'Initialer Zustand. Daten werden gesammelt, alle Felder editierbar. Noch keine ' +
      'Prüfung angestoßen.',
  },
  'status.planning': {
    title: 'In Planung',
    body:
      'Aktive Bearbeitung durch den Planer. Berechnungen, Skizzen und Anlagen werden ' +
      'erstellt. Kein Behördenstatus.',
  },
  'status.in_review': {
    title: 'In Prüfung',
    body:
      'An interne/externe Prüfstelle übergeben. Inhaltliche Änderungen sollten nur ' +
      'in Abstimmung mit dem Prüfer erfolgen.',
  },
  'status.approved': {
    title: 'Freigegeben',
    body:
      'Durch Prüfer freigegeben. Brandschutznachweis kann eingereicht werden. ' +
      'Änderungen erzeugen eine neue Version.',
  },
  'status.completed': {
    title: 'Abgeschlossen',
    body:
      'Projekt revisionssicher gesperrt. Keine Änderungen mehr möglich — auch nicht ' +
      'über die API. Für nachträgliche Änderungen Folgeprojekt anlegen.',
  },

  // ────────────────────────────────────────────────────────────
  // dashboard.* — KPI- und Chart-Cards auf der Startseite
  // ────────────────────────────────────────────────────────────
  'dashboard.activeProjects': {
    title: 'Aktive Projekte',
    body:
      'Anzahl der Projekte, die noch nicht abgeschlossen sind (Entwurf, Planung, ' +
      'in Prüfung, freigegeben). Klick öffnet die gefilterte Projektliste.',
  },
  'dashboard.upcomingDeadlines': {
    title: 'Fristen 30 Tage',
    body:
      'Aktive Projekte mit Frist in den nächsten 30 Tagen — überfällige zählen ' +
      'hier nicht mit. Die Liste „Fristen" unten rechts zeigt beides: ' +
      'überfällige Fristen rot markiert zuerst, danach die anstehenden. Klick ' +
      'öffnet die Projektliste, nach Frist aufsteigend sortiert.',
  },
  'dashboard.aiSuggestions': {
    title: 'KI-Vorschläge',
    body:
      'Offene KI- oder Kopier-Vorschläge auf Feld-Ebene über alle Ihre sichtbaren ' +
      'Projekte hinweg. Ein Vorschlag bleibt offen, bis Sie ihn übernehmen, ' +
      'ersetzen oder verwerfen. Klick öffnet die Vorschlags-Inbox; der ' +
      'Mauszeiger-Tooltip zeigt vorab die sechs wichtigsten.',
  },
  'dashboard.documents': {
    title: 'Dokumente',
    body:
      'Große Zahl: alle für Sie sichtbaren Dokumente — Dokumente Ihrer ' +
      'sichtbaren Projekte plus Ihre eigenen Uploads. Darunter die Aufteilung ' +
      'in „zugeordnet" (hängen an einem Projekt) und „offen" (liegen noch im ' +
      'Eingang). Klick öffnet die Übersicht „Alle Dokumente".',
  },
  'dashboard.recentProjects': {
    title: 'Aktuelle Projekte',
    body:
      'Die fünf zuletzt geänderten Projekte — nach Bearbeitungs-Zeitstempel. ' +
      'Nicht dasselbe wie „Offene Vervollständigung": dort geht es um ' +
      'unvollständige Pflichtfelder, hier nur um die zeitliche Reihenfolge.',
  },
  'dashboard.incompleteProjects': {
    title: 'Offene Vervollständigung',
    body:
      'Die fünf aktiven Projekte mit den meisten noch fehlenden Pflicht- und ' +
      'Schlüsselfeldern. Abgeschlossene und 100 %-Projekte sind ausgeblendet. ' +
      'Hilft zu sehen, wo noch Arbeit ansteht — unabhängig davon, wann zuletzt ' +
      'gearbeitet wurde.',
  },
  'dashboard.projectsByStatus': {
    title: 'Projekte nach Status',
    body:
      'Verteilung Ihrer sichtbaren Projekte auf die Workflow-Stati. Die Reihenfolge ' +
      'links → rechts spiegelt den typischen Lebenszyklus: Entwurf → Planung → ' +
      'in Prüfung → freigegeben → abgeschlossen.',
  },
  'dashboard.activity': {
    title: 'Aktivität (7 Tage)',
    body:
      'Anzahl der Audit-Log-Ereignisse pro Tag der letzten Woche (Anlegen, ' +
      'Bearbeiten, Statuswechsel, Uploads). Administratoren sehen alle ' +
      'Ereignisse, alle anderen nur die eigenen. Spitzen markieren intensive ' +
      'Bearbeitungstage.',
  },
  'dashboard.map': {
    title: 'Projekte auf der Karte',
    body:
      'Alle sichtbaren Projekte mit geocodeter Adresse als Marker; die Farbe ' +
      'spiegelt den Workflow-Status. Der gelbe Punkt markiert den Büro-Standort. ' +
      'Projekte ohne lat/lng werden hier nicht angezeigt — entweder wartet das ' +
      'Geocoding noch oder die Adresse konnte nicht aufgelöst werden.',
  },

  // ────────────────────────────────────────────────────────────
  // liegenschaft.* — Objektbetreuung
  // ────────────────────────────────────────────────────────────
  'liegenschaft.unterlagen': {
    title: 'Übernahme-Checkliste',
    body:
      'Die Soll-Liste der Unterlagen, die Sie bei Übernahme eines Objekts als ' +
      'Brandschutzbeauftragter erwarten. Jeder Eintrag nennt seine Fundstelle. ' +
      'Fehlt eine Unterlage, halten Sie die Stellungnahme des Auftraggebers und ' +
      'Ihre Bewertung fest. Der Katalog ist ein belegter Entwurf ohne fachliche Freigabe.',
    legalRef: 'DGUV Information 205-003 · ASR A2.2 / A2.3 · BayBO Art. 62b',
  },
};

/**
 * Sicherer Lookup. Gibt `null` zurück, wenn der Key unbekannt ist —
 * so kann `<HelpIcon>` einfach nichts rendern statt zu crashen.
 */
export function getTooltip(key: string): TooltipEntry | null {
  return TOOLTIPS[key] ?? null;
}
